/**
 * main.ts
 *
 * Bootstraps Vuetify and other plugins then mounts the App`
 */

// Components
import App from './App.vue'

// Composables
import {createApp} from 'vue'
import router from './router/router'

// Plugins
import {registerPlugins} from './plugins'
import axios from "axios";
import {installTokenRefreshInterceptor} from "@/composables/useTokenRefreshInterceptor";
import vuetify from "@/plugins/vuetify";
import pinia from "@/plugins/pinia";
import i18n, { initializeI18n } from "@/plugins/i18n";
import highlight from "@/plugins/highlight";
import { setupServiceWorkerUpdate } from '@/util/serviceWorkerUpdate'

import { getDevSimulatorHeaders } from '@/util/devSimulatorHeaders'

const bootstrap = async () => {
    await initializeI18n()

    const app = createApp(App)
    axios.defaults.baseURL = (import.meta.env.VITE_BASE_URL || '') + (import.meta.env.VITE_API_URL || '/api');

    axios.defaults.headers.post['Content-Type'] = 'application/json';
    axios.defaults.headers.post['Access-Control-Allow-Headers'] = 'Origin, Content-Type, X-Auth-Token, Authorization, Accept,charset,boundary,Content-Length';
    axios.defaults.withCredentials = true;

    // Global interceptor: transparently refresh expired JWT access tokens
    // and replay the original request (covers all stores, including those
    // that don't use the rootAuth wrapper).
    installTokenRefreshInterceptor();

    // Dev simulator: inject simulation headers into every request (non-production only)
    // sessionStorage ('dev-simulator-tab') overrides localStorage ('dev-simulator')
    // so different tabs can simulate different users for collaboration testing.
    if (import.meta.env.VITE_MODE !== 'production') {
        // Auto-initialize per-tab simulation from ?devUser= query parameter
        const devUserParam = new URLSearchParams(globalThis.location.search).get('devUser')
        if (devUserParam) {
            sessionStorage.setItem(
                'dev-simulator-tab',
                JSON.stringify({ user: devUserParam, roles: [], services: [] }),
            )
            // Remove query param from URL to keep it clean
            const url = new URL(globalThis.location.href)
            url.searchParams.delete('devUser')
            globalThis.history.replaceState({}, '', url.toString())
        }

        axios.interceptors.request.use((config) => {
            const simHeaders = getDevSimulatorHeaders()
            Object.entries(simHeaders).forEach(([key, value]) => {
                config.headers[key] = value
            })
            return config
        })
    }

    registerPlugins(app)

    app.use(pinia)
    app.use(router)
    app.use(vuetify)
    app.use(i18n)
    app.use(highlight)
    app.mount('#app')

    setupServiceWorkerUpdate()
}

void bootstrap()
